/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/tenancy/TenantProvider.tsx
 * layer: web
 * area: providers
 * kind: source
 * web_root: providers
 * relpath: web/src/providers/TenantProvider.tsx
 * filename: TenantProvider.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, rowShape, unwrapList } from '@/platform/api-client/client';
import type {
  Tenant,
  TenantAccess,
  GetAccessibleTenantsResponse,
  SwitchTenantResponse,
} from '@/platform/api-client/types';
import { useAuth } from '@/platform/auth/useAuth';

export interface TenantContextValue {
  currentTenant: Tenant | null;
  tenants: TenantAccess[];
  isLoading: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenants: () => Promise<void>;
  tenantType: string;
  modulesEnabled: string[];
  isImpersonating: boolean;
  exitImpersonation: () => Promise<void>;
  isModuleEnabled: (module: string) => boolean;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

const CURRENT_TENANT_KEY = 'current_tenant_id';

export function TenantProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, user, mfaRequired, mfaEnroll } = useAuth();
  const queryClient = useQueryClient();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<TenantAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Which auth state the last tenant load settled for. While signed-out the
  // provider settles immediately with no tenants; when auth then resolves,
  // there is a render (before the reload effect runs) where tenantType still
  // reads the 'msp' default. Reporting that window as loading stops guards
  // such as PlatformShell from redirecting a platform user on refresh.
  const [settledFor, setSettledFor] = useState<boolean | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Derive type and modules from the current tenant's access record
  const currentTenantAccess = tenants.find((t) => t.tenantId === currentTenant?.id);
  const tenantType = currentTenantAccess?.tenantType || 'msp';
  const modulesEnabled = currentTenantAccess?.modulesEnabled || ['core'];

  // isModuleEnabled: platform tenants bypass all module gates
  const isModuleEnabled = useCallback(
    (module: string) => {
      if (tenantType === 'platform') return true;
      return modulesEnabled.includes(module);
    },
    [tenantType, modulesEnabled]
  );

  // Load a specific tenant's details
  const loadTenant = useCallback(async (tenantId: string) => {
    try {
      const response = await api.get<Tenant>(`/tenants/${tenantId}`);
      setCurrentTenant(response.data);
      localStorage.setItem(CURRENT_TENANT_KEY, tenantId);
    } catch (error) {
      console.error('Failed to load tenant:', error);
      throw error;
    }
  }, []);

  // Internal base switch — performs the actual API call and state update
  const switchTenantBase = useCallback(
    async (tenantId: string) => {
      if (!tenantId || tenantId === currentTenant?.id) return;

      const hasAccess = tenants.some((t) => t.tenantId === tenantId);
      if (!hasAccess) {
        throw new Error('No access to tenant');
      }

      try {
        // Log the switch on the server — cookie-based auth handles token refresh
        const response = await api.post<SwitchTenantResponse>(`/tenants/switch/${tenantId}`);
        const switchedTenant = response.data?.tenant ?? response.data;

        // Update local tenant state (don't call setToken — cookie auth handles it)
        if (switchedTenant?.id) {
          setCurrentTenant(switchedTenant);
          localStorage.setItem(CURRENT_TENANT_KEY, tenantId);
        } else {
          // Fallback to separate fetch if backend didn't include tenant
          await loadTenant(tenantId);
        }

        // Invalidate all queries so data refetches for the new tenant
        queryClient.invalidateQueries();
      } catch (error) {
        console.error('Failed to switch tenant:', error);
        throw error;
      }
    },
    [currentTenant?.id, tenants, loadTenant, queryClient]
  );

  // Switch to a different tenant — tracks impersonation when crossing platform→msp boundary
  const switchTenant = useCallback(
    async (tenantId: string) => {
      // If we're on a platform tenant switching to an MSP, record impersonation origin
      if (tenantType === 'platform' && !isImpersonating) {
        const targetAccess = tenants.find((t) => t.tenantId === tenantId);
        if (targetAccess?.tenantType === 'msp') {
          sessionStorage.setItem('impersonating_from', currentTenant?.id || '');
        }
      }
      await switchTenantBase(tenantId);
      setIsImpersonating(!!sessionStorage.getItem('impersonating_from'));
    },
    [tenantType, isImpersonating, tenants, currentTenant?.id, switchTenantBase]
  );

  // exitImpersonation: return to the platform tenant that initiated the impersonation
  const exitImpersonation = useCallback(async () => {
    const platformTenantId = sessionStorage.getItem('impersonating_from');
    if (platformTenantId) {
      sessionStorage.removeItem('impersonating_from');
      await switchTenantBase(platformTenantId);
      setIsImpersonating(false);
    }
  }, [switchTenantBase]);

  // Do not start protected application requests until the authentication
  // provider has cleared challenge/enrolment. Those requests correctly return
  // mfa_required, but issuing them from an auth surface makes the global 403
  // handler redirect away from enrolment and can create a challenge loop.
  const canLoadProtectedTenantData = isAuthenticated && !mfaRequired && !mfaEnroll;

  // Fetch accessible tenants when fully authenticated
  const refreshTenants = useCallback(async () => {
    if (!canLoadProtectedTenantData) {
      setTenants([]);
      setCurrentTenant(null);
      setIsLoading(false);
      setSettledFor(false);
      return;
    }

    try {
      const response = await api.get<GetAccessibleTenantsResponse>('/tenants/accessible');
      const data = response.data;

      // TenantHandler.Accessible writes a bare array; the shared unwrapper
      // also accepts {data}. Drift throws into the catch below instead of
      // silently leaving the operator with no tenants.
      const tenantList = unwrapList(
        data,
        rowShape<TenantAccess>({ tenantId: 'string' }),
        'Accessible tenants'
      );
      setTenants(tenantList);

      // Restore saved tenant or use current from response or user's default
      const savedTenantId = localStorage.getItem(CURRENT_TENANT_KEY);
      let tenantToLoad = savedTenantId;

      if (!tenantToLoad && !Array.isArray(data) && data?.current) {
        tenantToLoad = data.current;
      }

      if (!tenantToLoad && user?.tenantId) {
        tenantToLoad = user.tenantId;
      }

      if (!tenantToLoad && tenantList.length > 0) {
        tenantToLoad = tenantList[0].tenantId;
      }

      if (tenantToLoad) {
        try {
          await loadTenant(tenantToLoad);
        } catch {
          // Stale tenant ID — clear and fall back to first accessible tenant
          localStorage.removeItem(CURRENT_TENANT_KEY);
          if (tenantList.length > 0) {
            const fallbackId = tenantList[0].tenantId;
            await loadTenant(fallbackId);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setIsLoading(false);
      setSettledFor(true);
    }
  }, [canLoadProtectedTenantData, user?.tenantId, loadTenant]);

  // Track impersonation state from sessionStorage whenever the active tenant changes
  useEffect(() => {
    setIsImpersonating(!!sessionStorage.getItem('impersonating_from'));
  }, [currentTenant?.id]);

  // Initial load
  useEffect(() => {
    refreshTenants();
  }, [refreshTenants]);

  return (
    <TenantContext
      value={{
        currentTenant,
        tenants,
        isLoading: isLoading || settledFor !== canLoadProtectedTenantData,
        switchTenant,
        refreshTenants,
        tenantType,
        modulesEnabled,
        isImpersonating,
        exitImpersonation,
        isModuleEnabled,
      }}
    >
      {children}
    </TenantContext>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
}
